import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  query, 
  QueryConstraint
} from 'firebase/firestore/lite';
import { db } from './config';

// Type alias for Firestore document data (lite version doesn't export this type)
type DocumentData = Record<string, any>;

/**
 * Get a single document by ID from a collection
 */
export async function getDocument<T = DocumentData>(
  collectionName: string,
  documentId: string
): Promise<T | null> {
  try {
    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as T;
    }
    return null;
  } catch (error) {
    console.error(`Error getting document ${documentId} from ${collectionName}:`, error);
    throw error;
  }
}

/**
 * Get all documents from a collection
 */
export async function getCollection<T = DocumentData>(
  collectionName: string
): Promise<T[]> {
  try {
    const querySnapshot = await getDocs(collection(db, collectionName));
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as T[];
  } catch (error) {
    console.error(`Error getting collection ${collectionName}:`, error);
    throw error;
  }
}

/**
 * Get documents from a collection with query constraints
 */
export async function queryCollection<T = DocumentData>(
  collectionName: string,
  ...queryConstraints: QueryConstraint[]
): Promise<T[]> {
  try {
    const q = query(collection(db, collectionName), ...queryConstraints);
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as T[];
  } catch (error) {
    console.error(`Error querying collection ${collectionName}:`, error);
    throw error;
  }
}

/**
 * Example usage:
 * 
 * // Get a single document
 * const user = await getDocument<User>('users', 'user123');
 * 
 * // Get all documents
 * const allUsers = await getCollection<User>('users');
 * 
 * // Query with constraints
 * import { where } from 'firebase/firestore/lite';
 * const activeUsers = await queryCollection<User>('users', where('active', '==', true));
 */
